/**
 * Add Kanban Column Modal
 * Allows users to create new workflow columns on the fly (Jira-style)
 */

'use client';

import React, { useState } from 'react';
import { X, Plus, Check } from 'lucide-react';

interface WorkflowState {
  key: string;
  label: string;
  color: string;
  description?: string;
}

interface AddColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (column: Omit<WorkflowState, 'key'>) => Promise<void>;
  existingColumns: WorkflowState[];
}

const AVAILABLE_COLORS = [
  { value: 'gray', label: 'Gray', preview: 'bg-gray-500' },
  { value: 'blue', label: 'Blue', preview: 'bg-blue-500' },
  { value: 'green', label: 'Green', preview: 'bg-green-500' },
  { value: 'yellow', label: 'Yellow', preview: 'bg-yellow-500' },
  { value: 'orange', label: 'Orange', preview: 'bg-orange-500' },
  { value: 'red', label: 'Red', preview: 'bg-red-500' },
  { value: 'purple', label: 'Purple', preview: 'bg-purple-500' },
  { value: 'pink', label: 'Pink', preview: 'bg-pink-500' },
  { value: 'indigo', label: 'Indigo', preview: 'bg-indigo-500' },
  { value: 'teal', label: 'Teal', preview: 'bg-teal-500' },
  { value: 'cyan', label: 'Cyan', preview: 'bg-cyan-500' },
  { value: 'emerald', label: 'Emerald', preview: 'bg-emerald-500' },
];

export default function AddColumnModal({ isOpen, onClose, onAdd, existingColumns }: AddColumnModalProps) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState('blue');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!label.trim()) {
      setError('Column name is required');
      return;
    }

    if (existingColumns.some(col => col.label.toLowerCase() === label.toLowerCase())) {
      setError('A column with this name already exists');
      return;
    }

    try {
      setIsSubmitting(true);
      
      await onAdd({
        label: label.trim(),
        color: selectedColor,
        description: description.trim() || undefined
      });

      // Reset form
      setLabel('');
      setDescription('');
      setSelectedColor('blue');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add column');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setLabel('');
      setDescription('');
      setSelectedColor('blue');
      setError('');
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full pointer-events-auto transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add Column</h2>
              <p className="text-sm text-gray-500 mt-1">Create a new workflow status column</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Column Name */}
            <div>
              <label htmlFor="column-name" className="block text-sm font-medium text-gray-700 mb-2">
                Column Name <span className="text-red-500">*</span>
              </label>
              <input
                id="column-name"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., In Review, Testing, QA"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="column-description" className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                id="column-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this status represent?"
                rows={3}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Color Theme
              </label>
              <div className="grid grid-cols-6 gap-2">
                {AVAILABLE_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setSelectedColor(color.value)}
                    disabled={isSubmitting}
                    className={`relative aspect-square rounded-lg ${color.preview} transition-all hover:scale-110 disabled:opacity-50 ${
                      selectedColor === color.value ? 'ring-4 ring-offset-2 ring-blue-500 scale-110' : ''
                    }`}
                    title={color.label}
                  >
                    {selectedColor === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Check className="w-5 h-5 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Preview</p>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium shadow-sm ${
                AVAILABLE_COLORS.find(c => c.value === selectedColor)?.preview || 'bg-blue-500'
              }`}>
                <div className="w-2 h-2 bg-white rounded-full" />
                {label || 'New Column'}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !label.trim()}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Column
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
